import React, { useState, useEffect } from "react";
import { Card, Select, Button, Tag, message, Spin } from "antd";
import {
  EnvironmentOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  workLocationsAPI,
  WorkLocation,
} from "../../../../utils/api/workLocationsAPI";
import {
  getUserWorkLocationsHelper,
  getClientLocationsHelper,
} from "../../../../utils/helpers/workLocationsHelper";
import { clearUserWorkLocations } from "../../../../utils/redux/slices/workLocationsSlice";
import { useAuth } from "../../../../utils/useAuth";
import { isAdmin, isSuperAdmin } from "../../../../utils/permissions";
import { isLocationActive } from "../../../../utils/helpers/locationVisibility";
import "./UserWorkLocations.scss";

interface UserWorkLocationsProps {
  userDetails: any;
  onUpdate?: () => void;
}

const UserWorkLocations: React.FC<UserWorkLocationsProps> = ({
  userDetails,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authUser: any = useAuth();
  const workLocationsState = useSelector((state: any) => state.workLocations);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allLocations, setAllLocations] = useState<WorkLocation[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [currentWorkLocations, setCurrentWorkLocations] = useState<
    WorkLocation[]
  >([]);

  const canEdit = isAdmin(authUser?.roles) || isSuperAdmin(authUser?.roles);

  const loadLocations = React.useCallback(async () => {
    try {
      const clientId =
        typeof userDetails?.user?.clientId === "object"
          ? userDetails.user.clientId._id
          : userDetails?.user?.clientId;

      if (!clientId) return;

      const locations = await getClientLocationsHelper(
        clientId,
        authUser.accessToken,
        dispatch,
        workLocationsState,
      );
      setAllLocations((locations || []).filter(isLocationActive));
    } catch (error) {
      console.error("Error loading locations:", error);
      message.error(t("Failed to load locations"));
    }
  }, [
    userDetails?.user?.clientId,
    authUser.accessToken,
    dispatch,
    workLocationsState,
    t,
  ]);

  const loadUserWorkLocations = React.useCallback(async () => {
    try {
      if (!userDetails?.user?._id) return;

      setLoading(true);
      const workLocs = await getUserWorkLocationsHelper(
        userDetails.user._id,
        authUser.accessToken,
        dispatch,
        workLocationsState,
      );
      setCurrentWorkLocations(workLocs);
      setSelectedLocations(workLocs.map((loc: WorkLocation) => loc._id) || []);
    } catch (error) {
      console.error("Error loading user work locations:", error);
    } finally {
      setLoading(false);
    }
  }, [
    userDetails?.user?._id,
    authUser.accessToken,
    dispatch,
    workLocationsState,
  ]);

  useEffect(() => {
    loadLocations();
    loadUserWorkLocations();
  }, [loadLocations, loadUserWorkLocations]);

  const handleSave = async () => {
    try {
      setLoading(true);
      await workLocationsAPI.assignWorkLocations(
        {
          userId: userDetails.user._id,
          locationIds: selectedLocations,
        },
        authUser.accessToken,
      );

      // Clear Redux cache for this user to force fresh data on next load
      dispatch(clearUserWorkLocations(userDetails.user._id));

      message.success(t("Work locations updated successfully"));
      setIsEditing(false);
      loadUserWorkLocations();

      if (onUpdate) {
        onUpdate();
      }
    } catch (error: any) {
      console.error("Error updating work locations:", error);
      message.error(
        error?.response?.data?.message || t("Failed to update work locations"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedLocations(
      currentWorkLocations.map((loc: WorkLocation) => loc._id),
    );
    setIsEditing(false);
  };

  return (
    <Card
      className="userWorkLocationsCard"
      title={
        <div className="cardTitle">
          <EnvironmentOutlined style={{ marginRight: 8 }} />
          {t("Work Locations")}
        </div>
      }
      extra={
        canEdit &&
        !isEditing && (
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => setIsEditing(true)}
          >
            {t("Edit")}
          </Button>
        )
      }
    >
      <Spin spinning={loading}>
        <div className="workLocationsContent">
          {isEditing ? (
            <div className="editMode">
              <div className="selectWrapper">
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder={t("Select work locations")}
                  value={selectedLocations}
                  onChange={setSelectedLocations}
                  options={allLocations
                    .filter((loc) => !loc.frozen)
                    .map((loc) => ({
                      label: loc.name,
                      value: loc._id,
                    }))}
                  maxTagCount="responsive"
                />
              </div>
              <div className="actionButtons">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={loading}
                >
                  {t("Save")}
                </Button>
                <Button icon={<CloseOutlined />} onClick={handleCancel}>
                  {t("Cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="viewMode">
              {currentWorkLocations.length > 0 ? (
                <div className="locationTags">
                  {currentWorkLocations.map((location) => (
                    <Tag
                      key={location._id}
                      icon={<EnvironmentOutlined />}
                      color="blue"
                      className="locationTag"
                    >
                      {location.name}
                    </Tag>
                  ))}
                </div>
              ) : (
                <div className="emptyState">
                  <EnvironmentOutlined
                    style={{ fontSize: 48, color: "#d9d9d9" }}
                  />
                  <p>{t("No work locations assigned")}</p>
                  {canEdit && (
                    <Button type="dashed" onClick={() => setIsEditing(true)}>
                      {t("Assign Locations")}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Spin>
    </Card>
  );
};

export default UserWorkLocations;
